import type {
  AuditFilters,
  AuditRow,
  CreatedSchool,
  CreateSchoolBody,
  PlatformOverview,
  ProvisionedAdmin,
  ReleaseView,
  SchoolMetrics,
  SchoolSummary,
} from "../../../shared/api/platform";
import type { Region, SchoolStatus } from "../../../shared/types/prisma-enums";

/**
 * F1–F5's decisions, pure.
 *
 * 🔴 EVERY BRANCH WORTH TESTING LIVES HERE AND NOT IN A COMPONENT. The repo has
 * no DOM renderer (OD-60) and may not gain one, so a decision made inside a
 * `.tsx` is a decision no test can reach — `SchoolAdminScreens.tsx` states the
 * rule and this tier needs it more, because two of these screens are
 * irreversible: archiving a school cannot be undone, and publishing content
 * changes what every school sees.
 */

/* ─────────────────────────────────────────────────────────── F1 ── */

/**
 * 🔴 WORST FIRST, AND THE SERVER ALREADY SORTED IT — SO THIS DOES NOT RE-SORT.
 *
 * `foldPlatform` orders `atRisk` by engagement ascending because *"a list sorted
 * by name buries the school about to churn at the letter S"*. Re-sorting in the
 * browser would be a second ordering rule, and the two would disagree the day
 * either moved. What this adds is the only thing the server cannot: the
 * **order the operator asked for** on the full table below.
 */
export type SchoolSort = "risk" | "name" | "largest" | "quietest";

export const SCHOOL_SORTS = ["risk", "name", "largest", "quietest"] as const;

/** Worst verdict first. A word cannot be averaged, but it can be ranked. */
const VERDICT_RANK: Record<"AT_RISK" | "WATCH" | "OK", number> = {
  AT_RISK: 0,
  WATCH: 1,
  OK: 2,
};

export function orderSchools(
  rows: readonly SchoolMetrics[],
  sort: SchoolSort,
): SchoolMetrics[] {
  const out = [...rows];
  switch (sort) {
    case "name":
      return out.sort((a, b) => a.name.localeCompare(b.name));
    case "largest":
      return out.sort(
        (a, b) =>
          b.enrolledStudents - a.enrolledStudents ||
          a.name.localeCompare(b.name),
      );
    case "quietest":
      /*
       * ⚠️ A SCHOOL WITH NO SEATS SORTS LAST, NOT FIRST. `engagement` is `null`
       * there — never `0`, because "nobody plays" and "there is nobody" are
       * different facts (`platform-metrics.ts`) — and treating null as zero
       * would put every brand-new school at the top of a list about schools that
       * have gone quiet.
       */
      return out.sort(
        (a, b) =>
          (a.engagement ?? Number.POSITIVE_INFINITY) -
            (b.engagement ?? Number.POSITIVE_INFINITY) ||
          a.name.localeCompare(b.name),
      );
    default:
      return out.sort(
        (a, b) =>
          VERDICT_RANK[a.health.verdict] - VERDICT_RANK[b.health.verdict] ||
          a.name.localeCompare(b.name),
      );
  }
}

/* ──────────────────────────────── F1 · the dashboard's two ranked cards ── */

/**
 * One row of *"Top Institutions by Engagement"*.
 *
 * 🔴 THE PAIR TRAVELS WITH THE RATE, NEVER THE RATE ALONE — OD-39/OD-72. "88%"
 * over three children and "88%" over four hundred are different findings, and a
 * card that ranks schools by the first one sends an operator to congratulate a
 * school that has barely started.
 */
export interface SchoolEngagementRow {
  schoolId: string;
  name: string;
  activeStudents: number;
  enrolledStudents: number;
  /** A whole-number percentage of the enrolled children who played. */
  ratePct: number;
}

/**
 * The schools whose children are actually playing, best first.
 *
 * 🔴 A SCHOOL WITH NO SEATS IS EXCLUDED, NOT RANKED LAST. This is a *top* list —
 * a school with nobody enrolled has no engagement to be top of, and 0/0 is not a
 * zero, it is an absence (`platform-metrics.ts`). Padding five slots with
 * schools that have nothing to measure is how this card ends up looking full on
 * a platform where nobody has played. `orderSchools(_, "quietest")` sorts them
 * last instead of dropping them because that list is a census; this is a ranking.
 */
export function topSchoolsByEngagement(
  rows: readonly SchoolMetrics[],
  limit = 5,
): SchoolEngagementRow[] {
  return rows
    .filter((r) => r.enrolledStudents > 0)
    .map((r) => ({
      schoolId: r.schoolId,
      name: r.name,
      activeStudents: r.activeStudents,
      enrolledStudents: r.enrolledStudents,
      /* ⚠️ DERIVED FROM THE PAIR THIS ROW ALSO PRINTS, not from `engagement`.
         The two agree today; computing the percentage from the very numbers
         shown beside it is what keeps them agreeing after either one moves. */
      ratePct: Math.round((r.activeStudents / r.enrolledStudents) * 100),
    }))
    .sort(
      (a, b) =>
        b.ratePct - a.ratePct ||
        b.activeStudents - a.activeStudents ||
        a.name.localeCompare(b.name),
    )
    .slice(0, limit);
}

/** One row of *"Top Games by Plays"*. */
export interface GamePlayRow {
  /** The event-table id — "sky-quest". `GAME_TITLES` names it for a human. */
  game: string;
  /** Levels finished. This is what the ranking is on. */
  rounds: number;
  /** Distinct children who have played it. Never a count of sittings. */
  children: number;
  /** This game's share of every round played in the window, whole percent. */
  sharePct: number;
}

/**
 * The games that have actually been played, most first.
 *
 * 🔴 A GAME WITH ZERO ROUNDS IS DROPPED. `/superadmin/games` returns all five
 * whether or not anybody opened them — `gamesAvailable` sits on the summary
 * precisely so a screen can say "2 of 5 started" — and listing a zero under a
 * heading reading "Top Games by Plays" states that it is among the most played.
 * An empty list is the honest answer on a platform where nothing has been
 * played, and the card has a sentence for that.
 */
export function topGamesByPlays(
  games: readonly { game: string; rounds: number; children: number }[],
  limit = 5,
): GamePlayRow[] {
  const played = games.filter((g) => g.rounds > 0);
  /* The denominator is every round played, including the games that fall
     outside `limit` — a share of the visible five would sum to 100% and say
     something untrue about the platform. */
  const total = played.reduce((sum, g) => sum + g.rounds, 0);
  return played
    .map((g) => ({
      game: g.game,
      rounds: g.rounds,
      children: g.children,
      /* `total` cannot be 0: the filter guarantees at least one round. */
      sharePct: Math.round((g.rounds / total) * 100),
    }))
    .sort(
      (a, b) =>
        b.rounds - a.rounds ||
        b.children - a.children ||
        a.game.localeCompare(b.game),
    )
    .slice(0, limit);
}

/** The four counts F1's status row shows, in lifecycle order rather than alphabetical. */
export const STATUS_ORDER = [
  "ONBOARDING",
  "ACTIVE",
  "SUSPENDED",
  "ARCHIVED",
] as const satisfies readonly SchoolStatus[];

/**
 * 🔴 `CREATED` IS FOLDED INTO `ONBOARDING` FOR DISPLAY, AND HERE IS WHY THAT IS
 * NOT A LIE.
 *
 * `SchoolsService.create` never writes `CREATED` — `org.prisma` defaults the
 * column to `ONBOARDING` because *"creating a school through F2 IS the onboard
 * action"*. So a `CREATED` row can only come from a hand-written insert, and
 * showing an operator a permanently-zero tile teaches them a state exists that
 * the product cannot produce. The count is added rather than dropped, so a row
 * that does exist is never invisible.
 */
export function statusCounts(
  overview: PlatformOverview,
): { status: SchoolStatus; count: number }[] {
  const by = overview.schools.byStatus;
  return STATUS_ORDER.map((status) => ({
    status,
    count: status === "ONBOARDING" ? by.ONBOARDING + by.CREATED : by[status],
  }));
}

/**
 * The one sentence F1 leads with.
 *
 * 🔴 A PAIR, ALWAYS — never the rate alone. OD-39/OD-72: *"30% engagement is a
 * grade whose meaning depends entirely on what it was divided by."* `null` when
 * there are no seats at all, so the screen says "no children yet" rather than
 * rendering "0 of 0" as though that were a finding.
 */
export function engagementPair(
  overview: PlatformOverview,
): { count: number; total: number; days: number } | null {
  const total = overview.enrolment.seats;
  if (total === 0) return null;
  return {
    count: overview.engagement.activeStudents,
    total,
    days: overview.engagement.windowDays,
  };
}

/* ─────────────────────────────────────────────────────────── F2 ── */

/**
 * 🔴 THE BUTTONS COME FROM `legalNextStatuses`, WHICH THE SERVER SENDS. This
 * function only decides which of them needs confirming.
 *
 * D4's lesson: *"the UI must not offer a promote button that no-ops or throws."*
 * A second transitions table in the browser would disagree with
 * `school-lifecycle.ts` the first time either moved — and the disagreement
 * would look like a missing button, not a bug.
 */
export function needsConfirmation(to: SchoolStatus): boolean {
  /*
   * ⚠️ ARCHIVE ONLY. Suspension is reversible (`SUSPENDED → ACTIVE` is in the
   * table) and confirming it would train an operator to click through dialogs,
   * which is how the one irreversible dialog gets clicked through too.
   * `school-lifecycle.ts` is explicit that the confirmation for archive
   * *"belongs on the screen"* — this is that screen's half of the contract.
   */
  return to === "ARCHIVED";
}

/** Nothing left to move it to. A terminal school offers no buttons at all. */
export function isTerminal(school: SchoolSummary): boolean {
  return school.legalNextStatuses.length === 0;
}

/**
 * 🔴 THE SCHOOLS AN OPERATOR SHOULD DEAL WITH FIRST, AND IT IS NOT ALPHABETICAL.
 *
 * A school with zero admins cannot be used by anybody at all — `adminCount: 0`
 * is *"the state F2 exists to fix"* in `schools.service.ts`'s own words. Sorting
 * those to the top makes the list a work queue rather than a directory.
 */
export function orderSchoolAdmin(
  rows: readonly SchoolSummary[],
): SchoolSummary[] {
  return [...rows].sort(
    (a, b) =>
      Number(a.adminCount > 0) - Number(b.adminCount > 0) ||
      // Then archived last: they are read-only and there is nothing to do.
      Number(a.status === "ARCHIVED") - Number(b.status === "ARCHIVED") ||
      a.name.localeCompare(b.name),
  );
}

/**
 * What went wrong, in the operator's terms.
 *
 * ⚠️ A 409 IS THE ONLY STATUS THIS TIER GETS THAT MEANS SOMETHING SPECIFIC.
 * `SchoolsService` answers `ConflictException` for a duplicate school name and
 * for a taken email — and unlike every other tier it answers *specifically*,
 * because *"the caller here is a SUPER_ADMIN whose scope is unrestricted by
 * design and audited, so hiding 'that address is taken' would only produce a
 * duplicate-account support ticket."* Collapsing it into a generic failure here
 * would throw that away on the client side.
 */
/**
 * 🔴 `NAME_TAKEN` AND `EMAIL_TAKEN` ARE SPLIT OUT OF `CONFLICT`, AND THE BUG
 * THAT FORCED IT IS WORTH KEEPING IN VIEW.
 *
 * `POST /superadmin/schools` answers 409 for two unrelated reasons — the
 * school's name is taken, or the first admin's email is — and the status is the
 * same for both. With only `CONFLICT` to work with, `CreateSchoolForm` passed
 * `t.nameTaken` as its sentence, so an operator whose *email* clashed was told
 * to rename the school. No rename could fix it, and the school never got
 * created; the form looked broken for every attempt.
 *
 * ⚠️ THE CODES COME FROM THE SERVER'S `code` FIELD, WHICH IS A SLUG. `client.ts`
 * refuses anything that is not SCREAMING_SNAKE, so this cannot become a channel
 * for echoing a value into the UI — the reason error bodies were unread at all.
 */
export type WriteFailure =
  "NAME_TAKEN" | "EMAIL_TAKEN" | "CONFLICT" | "REFUSED" | "INVALID" | "GENERIC";

export function classifyWriteError(err: unknown): WriteFailure {
  const status = (err as { status?: number } | null)?.status;
  if (status === 409) {
    /*
     * ⚠️ `CONFLICT` REMAINS THE FALLBACK, AND IT HAS TO. An older server, a
     * proxy that rewrote the body, or a conflict from a route that has not been
     * given a code yet all land here — and a generic "that did not work" is
     * correct then. Guessing a specific cause is what produced this bug.
     */
    const code = (err as { code?: unknown } | null)?.code;
    if (code === "SCHOOL_NAME_TAKEN") return "NAME_TAKEN";
    if (code === "ACCOUNT_EMAIL_TAKEN") return "EMAIL_TAKEN";
    return "CONFLICT";
  }
  if (status === 401 || status === 403) return "REFUSED";
  /*
   * 400 is the lifecycle refusing an illegal move — `planTransition` returns a
   * reason, and `BadRequestException` carries it. ⚠️ `client.ts` deliberately
   * does not read response bodies, so that reason never reaches here; the screen
   * shows its own sentence instead. 📋 A structured error body is the fix, and
   * it is a change to `client.ts` and `AllExceptionsFilter` together.
   */
  if (status === 400) return "INVALID";
  return "GENERIC";
}

/* ──────────────────────────────── F2 · the operator-chosen password ── */

/**
 * 🔴 A SECOND COPY OF `backend/src/modules/superadmin/password-policy.ts`, AND
 * THE DUPLICATION IS DELIBERATE — `IMPERSONATION_REASON_MIN` below makes the
 * identical argument for the identical reason.
 *
 * `client.ts` discards response bodies, so a rejected password comes back as a
 * bare 400 with no cause. An operator who typed a passphrase, clicked, waited,
 * and got *"couldn't do that"* has no way to learn that the problem was the
 * school's name inside it — so they try another one, and another. Checking here
 * means the refusal is a sentence under the field while it is still under the
 * cursor.
 *
 * ⚠️ THE SERVER'S COPY IS THE ONE THAT DECIDES, ALWAYS. This is a courtesy, not
 * a control: it runs in a browser the operator owns, and `SchoolsService`
 * re-runs the whole policy against the school row it actually loaded. If the two
 * ever disagree the server wins and the screen shows a generic failure — which
 * is worse copy, not a weaker rule.
 */
export const OPERATOR_PASSWORD_MIN = 12;
export const OPERATOR_PASSWORD_MAX = 128;

export type PasswordProblem =
  | "TOO_SHORT"
  | "TOO_LONG"
  | "EDGE_WHITESPACE"
  | "ONE_CLASS"
  | "PREDICTABLE"
  | "ECHOES_IDENTITY"
  /** ⚠️ CLIENT-ONLY. The server never sees the confirmation field. */
  | "MISMATCH";

/** Mirrors `PREDICTABLE_STEMS`. A short institutional list, not a dictionary. */
const PREDICTABLE_STEMS = [
  "welcome",
  "password",
  "passw0rd",
  "admin",
  "adminadmin",
  "letmein",
  "qwerty",
  "qwertyuiop",
  "iloveyou",
  "changeme",
  "kalqy",
  "school",
  "teacher",
  "principal",
  "abcdef",
  "asdfgh",
  "zxcvbn",
  "monkey",
  "dragon",
  "sunshine",
  "football",
  "india",
  "kerala",
];

/**
 * Strip the decoration people add to a blocked password, so the stem shows.
 *
 * ⚠️ NEVER APPLIED TO ANYTHING STORED OR SENT. It is only used on the candidate
 * being rejected — normalising before submitting would make `Welcome@123` and
 * `we1c0me123` the same credential.
 */
function normalise(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[@4]/g, "a")
    .replace(/3/g, "e")
    .replace(/[1!|]/g, "i")
    .replace(/0/g, "o")
    .replace(/[$5]/g, "s")
    .replace(/7/g, "t")
    .replace(/[^a-z]/g, "");
}

function classCount(raw: string): number {
  let n = 0;
  if (/[a-z]/.test(raw)) n += 1;
  if (/[A-Z]/.test(raw)) n += 1;
  if (/[0-9]/.test(raw)) n += 1;
  if (/[^a-zA-Z0-9]/.test(raw)) n += 1;
  return n;
}

/**
 * Words drawn from the two PUBLIC facts about this account: the login address
 * and the school's name. Under four characters is dropped — a school called "Sun
 * Public School" must not ban every password containing `sun`.
 */
function identityTokens(email: string, schoolName = ""): string[] {
  const local = email.split("@")[0] ?? "";
  return [
    local,
    ...local.split(/[^a-zA-Z0-9]+/),
    schoolName.replace(/[^a-zA-Z0-9]+/g, ""),
    ...schoolName.split(/[^a-zA-Z0-9]+/),
  ]
    .map((token) => token.toLowerCase().replace(/[^a-z0-9]/g, ""))
    .filter((token) => token.length >= 4);
}

/**
 * The first problem with a password, or `null`.
 *
 * 🔴 THE ORDER IS THE ORDER SOMEBODY FIXES THEM IN. Length before composition
 * before predictability: telling an operator their eight-character password also
 * echoes the school name is two corrections for one edit.
 *
 * ⚠️ `confirm` IS CHECKED LAST, DELIBERATELY. A mismatch reported first hides
 * the fact that the password was never going to be accepted anyway, and the
 * operator retypes a rejected string twice.
 */
export function passwordProblem(
  password: string,
  confirm: string,
  context: { email: string; schoolName?: string },
): PasswordProblem | null {
  if (password !== password.trim()) return "EDGE_WHITESPACE";
  if (password.length < OPERATOR_PASSWORD_MIN) return "TOO_SHORT";
  if (password.length > OPERATOR_PASSWORD_MAX) return "TOO_LONG";
  if (classCount(password) < 2) return "ONE_CLASS";

  const normalised = normalise(password);
  if (PREDICTABLE_STEMS.some((stem) => normalised.includes(stem)))
    return "PREDICTABLE";

  const flat = password.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (
    identityTokens(context.email, context.schoolName).some((token) =>
      flat.includes(token),
    )
  ) {
    return "ECHOES_IDENTITY";
  }

  if (confirm !== password) return "MISMATCH";
  return null;
}

/**
 * 🔴 STILL THREE STATES, AND THE FIRST ONE CHANGED MEANING.
 *
 * It was `{ kind: 'generate' }` — the empty form, which used to be a valid and
 * *safer* submission meaning *"the server picks one"*. There is no generated
 * path any more (`school.dto.ts` on the server records why it went with the
 * invitation), so an empty form is now a form that is not finished.
 *
 * ⚠️ IT IS `'empty'` RATHER THAN A `'problem'` WITH A `TOO_SHORT` PROBLEM, and
 * that distinction is the whole reason the state survived as its own kind. An
 * operator who has typed nothing has made no mistake — telling them their
 * password is too short before they have started is how a form teaches people to
 * ignore its warnings. `'empty'` renders as a plain instruction; `'problem'`
 * renders as a correction.
 */
export type PasswordState =
  | { kind: "empty" }
  | { kind: "ready"; password: string }
  | { kind: "problem"; problem: PasswordProblem };

export function passwordState(
  password: string,
  confirm: string,
  /**
   * ⚠️ `schoolName` IS OPTIONAL, AND THE CALLER THAT OMITS IT IS THE OPERATOR
   * FORM. A platform operator has no school, so there is no name for
   * `ECHOES_IDENTITY` to compare against — and passing `''` to satisfy a
   * required field is how a caller ends up passing `''` when there IS one.
   * Mirrors `PasswordContext` on the server.
   */
  context: { email: string; schoolName?: string },
): PasswordState {
  if (password.length === 0 && confirm.length === 0) return { kind: "empty" };
  const problem = passwordProblem(password, confirm, context);
  return problem === null
    ? { kind: "ready", password }
    : { kind: "problem", problem };
}

/* ─────────────────────────────────────────────────────────── F4 ── */

/**
 * Which release a school on the default channel currently sees.
 *
 * 🔴 THE NEWEST **PUBLISHED** ONE — BETA IS NOT IT, and a screen that showed
 * beta as "live" would be describing what only opted-in schools get. The server
 * owns this decision in `content-state.ts` for the gameplay path; this is the
 * read-only echo of it for the operator, and it is deliberately the *narrow*
 * half: it never claims to know which schools are opted in.
 */
export function liveRelease(
  releases: readonly ReleaseView[],
): ReleaseView | null {
  return releases.find((r) => r.state === "PUBLISHED") ?? null;
}

/** Newest first, matching the order the server already returns them in. */
export function orderReleases(releases: readonly ReleaseView[]): ReleaseView[] {
  return [...releases].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/**
 * ⚠️ RETIRING IS THE ONE CONTENT MOVE WORTH CONFIRMING, and publishing is not —
 * which is the opposite of the instinct.
 *
 * Publishing is forward and additive: schools start seeing a release. Retiring
 * *removes* something children are mid-way through, and while their history
 * survives (events carry the version), the next session will not offer it. One
 * of the two changes what a child sees tomorrow morning without anybody at the
 * school being told.
 */
export function releaseNeedsConfirmation(to: string): boolean {
  return to === "RETIRED";
}

/* ─────────────────────────────────────────────────────────── F5 ── */

/**
 * 🔴 EMPTY FILTER VALUES ARE DROPPED, NOT SENT BLANK.
 *
 * `auditQuerySchema` is `.strict()` with `min(1)` on every string filter, so
 * `?targetType=` is a **400** rather than an ignored parameter. A form whose
 * cleared input still submits its key breaks the page the moment an operator
 * types into a filter and deletes it again — during an incident, on the one
 * table people open precisely because they suspect something happened.
 *
 * ⚠️ `cursor` IS DELIBERATELY NOT CARRIED. Changing a filter must restart
 * paging: a cursor is a position in the *previous* result set, and reusing it
 * across a filter change silently skips every matching row before that point.
 * Skipping is the half that matters — a row you never see looks exactly like a
 * row that was never written.
 */
export function cleanAuditFilters(draft: Record<string, string>): AuditFilters {
  const out: AuditFilters = {};
  for (const [key, value] of Object.entries(draft)) {
    const trimmed = value.trim();
    if (trimmed.length === 0) continue;
    if (key === "cursor") continue;
    (out as Record<string, string>)[key] = trimmed;
  }
  return out;
}

/**
 * Is this range one the server will accept?
 *
 * 🔴 CHECKED HERE SO THE REFUSAL IS A SENTENCE RATHER THAN A 400.
 * `auditQuerySchema` refines `from <= to`, and a 400 arrives at `client.ts` as
 * a status with no body — so an operator who typed the dates backwards would
 * get "couldn't load this" and no idea why.
 */
export function rangeIsValid(from: string, to: string): boolean {
  if (from.length === 0 || to.length === 0) return true;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end)) return true;
  return start <= end;
}

/**
 * Append a page, keeping FIFO and never duplicating a row.
 *
 * 🔴 THE DEDUPE IS NOT DEFENSIVE PROGRAMMING — IT IS THE CURSOR'S CONTRACT MADE
 * VISIBLE. `AuditReadService.fetch` uses `skip: 1` because *"the cursor row is
 * the LAST row the caller already has; without it every page repeats its
 * predecessor's final row."* If that ever regresses, an operator counting
 * occurrences of an action in an incident would count one of them twice. Cheap
 * to hold here, and the duplicate would otherwise be invisible.
 */
export function appendAuditPage(
  existing: readonly AuditRow[],
  next: readonly AuditRow[],
): AuditRow[] {
  const seen = new Set(existing.map((r) => r.id));
  return [...existing, ...next.filter((r) => !seen.has(r.id))];
}

/**
 * The actor column.
 *
 * 🔴 A NULL ACTOR IS THE SYSTEM, AND IT IS NOT WRITTEN AS A NAME.
 * `audit-query.ts` makes the same choice for the CSV: writing the word `system`
 * into a column that also holds user ids invents a user called system. The
 * screen shows a label, and the label is visibly not an id.
 */
export function actorLabel(row: AuditRow, systemWord: string): string {
  if (row.actorId === null) return systemWord;
  return row.actorLabel.length > 0 ? row.actorLabel : row.actorId;
}

/* ────────────────────────────────────────── searching a long list ── */

/**
 * Filter a list of named things by what somebody typed.
 *
 * 🔴 CLIENT-SIDE, AND THAT IS A DECISION WITH A CEILING RATHER THAN AN
 * OVERSIGHT. `GET /superadmin/schools` returns every school in one grouped read
 * (`platform.service.ts`: *"a platform of 3 schools costs the same five round
 * trips as one of 300"*), so the rows are already here — a `?q=` parameter would
 * add a round trip per keystroke to fetch a subset of what the browser holds,
 * and ADR-009 ships no cache to absorb it.
 *
 * ⚠️ THE CEILING IS REAL AND WORTH NAMING: at a few thousand schools the payload
 * itself becomes the problem, and the fix then is server-side pagination on the
 * list endpoint — not a smarter filter here. 📋 Reported rather than assumed
 * away.
 *
 * 🔴 CASE- AND DIACRITIC-INSENSITIVE, because the alternative is a search box
 * that fails on the product's own audience. School names in Kerala are typed
 * with and without diacritics by different people on different keyboards, and a
 * naive `includes` would hide "Little Stars" from somebody who typed "little".
 */
export function matchesQuery(
  haystack: readonly (string | null | undefined)[],
  query: string,
): boolean {
  const needle = normaliseForSearch(query);
  if (needle.length === 0) return true;
  return haystack.some((value) =>
    normaliseForSearch(value ?? "").includes(needle),
  );
}

function normaliseForSearch(value: string): string {
  return (
    value
      .toLowerCase()
      /*
       * NFD splits an accented character into base + combining mark, and the
       * range below removes the marks — so "Kōchi" and "Kochi" match.
       *
       * ⚠️ IT ONLY STRIPS THE COMBINING-DIACRITICAL BLOCK, which is what makes
       * it safe on a school name typed in a non-Latin script: those marks are
       * separate code points outside that block and survive untouched. School
       * names are user data, and the product being English does not make them
       * Latin (ADR-044).
       */
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim()
  );
}

/**
 * Cut a list to a page, and say what was left out.
 *
 * 🔴 IT RETURNS `hidden` RATHER THAN JUST THE SLICE. The audit CSV, the rollout
 * plan and the health segments all follow the same rule in this codebase — *"no
 * silent caps"* — because a list showing twenty of two hundred without saying so
 * reads as "there are twenty".
 */
export function pageOf<T>(
  rows: readonly T[],
  limit: number,
): { rows: T[]; hidden: number } {
  if (limit <= 0 || rows.length <= limit) return { rows: [...rows], hidden: 0 };
  return { rows: rows.slice(0, limit), hidden: rows.length - limit };
}

/** How many rows a list shows before asking. Small enough to scan, big enough for a pilot. */
export const LIST_PAGE_SIZE = 25;

/* ─────────────────────────────────────────────────────────── X5 ── */

/**
 * An inverted size band, caught before the request.
 *
 * 🔴 `min: 50, max: 10` MATCHES NOTHING, and an empty plan reads as *"no school
 * qualifies"* rather than *"you typed the band backwards"* — on the one screen
 * whose entire job is to be read before content reaches other people's children.
 * The server refuses it too (`rolloutRuleSchema.refine`); this is so the operator
 * finds out while the box is still under their cursor.
 */
export function rangeBackwards(min: string, max: string): boolean {
  const lo = Number(min.trim());
  const hi = Number(max.trim());
  if (min.trim() === "" || max.trim() === "") return false;
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return false;
  return lo > hi;
}

/**
 * Form strings → the rule the API takes.
 *
 * 🔴 BLANK MEANS ABSENT, NOT ZERO. `rolloutRuleSchema` is `.strict()` with
 * `min(0)`, so sending `minStudents: 0` for an empty box is a *filter* — it
 * happens to match everything today, and would stop doing so the moment the
 * floor moved. An omitted key is the honest encoding of "the operator did not
 * say".
 *
 * ⚠️ AND AN EMPTY `regions` ARRAY IS OMITTED RATHER THAN SENT. The schema
 * accepts `[]`, and `planRollout` reads it as "match none" — so a form where
 * nobody ticked a region would return an empty plan instead of every region,
 * which is the opposite of what the unticked state means to a person.
 */
/*
 * ⚠️ GENERIC OVER THE REGION TYPE RATHER THAN TAKING `string[]`. This module is
 * deliberately pure, but widening to `string` here would hand the API client a
 * `string[]` where it wants `Region[]` — and the call site would then need a
 * cast, which is exactly how an unchecked value reaches a `.strict()` schema.
 */
export function buildRolloutRule<R extends string>(form: {
  regions: readonly R[];
  min: string;
  max: string;
  limit: string;
}): {
  regions?: R[];
  minStudents?: number;
  maxStudents?: number;
  limit?: number;
} {
  const num = (raw: string): number | undefined => {
    const trimmed = raw.trim();
    if (trimmed === "") return undefined;
    const value = Number(trimmed);
    return Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined;
  };

  const minStudents = num(form.min);
  const maxStudents = num(form.max);
  const limit = num(form.limit);

  return {
    ...(form.regions.length > 0 ? { regions: [...form.regions] } : {}),
    ...(minStudents !== undefined ? { minStudents } : {}),
    ...(maxStudents !== undefined ? { maxStudents } : {}),
    // `limit: 0` would be a plan that targets nobody, which no operator means.
    ...(limit !== undefined && limit > 0 ? { limit } : {}),
  };
}

/* ─────────────────────────────────────────────────────────── X4 ── */

/**
 * 🔴 THE FLOOR IS THE SERVER'S, RESTATED SO THE BUTTON CAN BE DISABLED RATHER
 * THAN THE REQUEST REFUSED.
 *
 * `impersonateSchema` demands a trimmed reason of ≥8 characters. This is a
 * deliberate second copy of that number — the alternative is submitting and
 * showing a 400, on the one action whose entire justification is that somebody
 * stated a purpose, and "say why" arriving *after* the click reads as the form
 * being broken rather than as a requirement.
 *
 * ⚠️ EIGHT IS A FLOOR AGAINST `"."`, NOT A QUALITY BAR. No schema can tell a
 * real reason from a plausible one; what it buys is that a deliberately empty
 * reason costs as much effort as a real one.
 */
export const IMPERSONATION_REASON_MIN = 8;

/**
 * ⚠️ NEWLINES ARE REJECTED, MATCHING `impersonateSchema`'s `one line` rule — a
 * pasted multi-line reason would pass a length check here and 400 at the server.
 */
export function reasonIsUsable(reason: string): boolean {
  const trimmed = reason.trim();
  return trimmed.length >= IMPERSONATION_REASON_MIN && !/[\n\r]/.test(trimmed);
}

/**
 * 🔴 AN IMPERSONATION SESSION EXPIRES ON A CLOCK NOBODY IS WATCHING, SO THE
 * SCREEN HAS TO DO THE WATCHING.
 *
 * The token is fifteen minutes and cannot be revoked (§8.1: verified by
 * signature, no store lookup), so "ended" means "expired" — and an operator who
 * believes they are still acting as someone else will read a 401 as the customer's
 * account being broken. Returns whole seconds remaining, floored at zero.
 */
export function secondsRemaining(
  startedAt: number,
  expiresInSeconds: number,
  now: number,
): number {
  const elapsed = Math.floor((now - startedAt) / 1000);
  return Math.max(0, expiresInSeconds - elapsed);
}

/** `m:ss`, because a bare second count is not a duration anyone reads at a glance. */
export function formatRemaining(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(mins)}:${secs.toString().padStart(2, "0")}`;
}

/* ────────────────────────────────────────────────────────── jobs ── */

/**
 * 🔴 `data-retention` IS THE ONE JOB A CLICK CAN DESTROY DATA WITH, so it is the
 * one that asks first.
 *
 * `jobs.controller.ts` audits it as `DATA_ERASURE` rather than
 * `SUPER_ADMIN_ACCESS` for exactly this reason. ⚠️ `RETENTION_DRY_RUN` may well
 * make the run harmless — but the flag lives in the environment, not on this
 * screen, so an operator cannot tell from here which mode they are in. The
 * confirmation is the honest response to that, and `JobRunResult.dryRun` in the
 * result is how they find out afterwards.
 */
export function jobNeedsConfirmation(job: string): boolean {
  return job === "data-retention";
}

/* ──────────────────────────────────────────────── F5 · reading a row ── */

/**
 * How many rows one page of the audit log carries.
 *
 * 🔴 FIFTEEN, AND THE NUMBER IS A READING DECISION RATHER THAN A TRANSPORT ONE.
 * The server's ceiling is `AUDIT_PAGE_MAX` (200) and the screen used to ask for
 * it, then grow the table with a "load more" button — so an operator working
 * through an incident ended up scrolling a single thousand-row table, losing
 * their place every time it grew, with no way to say *"the row I mean is the
 * fourth one on page three"*. Fifteen rows fit a laptop without scrolling, which
 * is what makes a page a place you can return to.
 *
 * ⚠️ IT DOES NOT CHANGE WHAT THE SERVER AUDITS. Reading the log writes one row
 * per request whatever the page size (`AuditReadService.list`), so a smaller
 * page means more rows in the artefact being read. That cost is real and is the
 * reason this is 15 rather than 5: small enough to read, large enough that a
 * day's work is a handful of pages.
 */
export const AUDIT_PAGE_SIZE = 15;

/**
 * Who did it, as a person rather than an identifier.
 *
 * 🔴 THIS IS THE CHANGE `actorLabel` COULD NOT MAKE ON ITS OWN. The stored label
 * is an id on purpose — `AuditService.describeActor`: *"a name here would put a
 * staff member's personal data into a table retained beyond the ordinary
 * window"* — and that rule is untouched. What changed is that the server now
 * JOINS the name on the way out (`ResolvedAuditRow`), so the column headed "Who"
 * can stop answering `u_cm8x1a9b0000qzab`.
 *
 * ⚠️ FOUR CASES, AND THEY ARE DIFFERENT FACTS RATHER THAN DEGREES OF THE SAME
 * ONE:
 *
 *   · **the system** — `actorId` is null. Not a user called "system".
 *   · **a person we can still name** — the ordinary case.
 *   · **a person who has been deleted** — an id with no name. The log surviving
 *     its actors is exactly what `actorLabel` was denormalised for, so this
 *     renders the id and says nothing more.
 *   · **an operator acting as somebody else** — `impersonatorId` is set, and the
 *     row must not read as the head teacher's own action. `admin-panel.md` §8 is
 *     written about that specific confusion.
 */
export interface AuditActor {
  kind: "SYSTEM" | "PERSON" | "GONE" | "IMPERSONATED";
  /** What to print. Never empty. */
  name: string;
  /** The second line, when there is one — an address, a role, or both. */
  detail: string | null;
  /** The operator behind an impersonated action. */
  via: string | null;
}

export function auditActor(row: AuditRow, systemWord: string): AuditActor {
  if (row.actorId === null) {
    return { kind: "SYSTEM", name: systemWord, detail: null, via: null };
  }

  const named = row.actorName !== null && row.actorName.length > 0;
  const name = named ? row.actorName! : (row.actorLabel || row.actorId);
  const detail =
    [row.actorEmail, row.actorRole].filter((part): part is string => part !== null && part.length > 0).join(" · ") ||
    null;

  if (row.impersonatorId !== null) {
    return {
      kind: "IMPERSONATED",
      name,
      detail,
      /* The id is shown when the name is gone, for the same reason the actor's
         is: an impersonation nobody can attribute is worse than an ugly id. */
      via: row.impersonatorName ?? row.impersonatorId,
    };
  }

  return { kind: named ? "PERSON" : "GONE", name, detail, via: null };
}

/**
 * What the row was about, as a thing rather than an id.
 *
 * ⚠️ THE TYPE IS ALWAYS SHOWN AND THE ID ONLY WHEN THERE IS NO NAME. "School ·
 * Little Stars" is readable; "School · Little Stars · s_cm7…" is a name with an
 * identifier stapled to it, and the identifier is what the filter box is for.
 * When nothing resolved, the id IS the fact the log recorded, so it is printed.
 */
export function auditTarget(row: AuditRow): { type: string; label: string | null } {
  return {
    type: row.targetType,
    label: row.targetLabel ?? row.targetId,
  };
}

/**
 * A page cursor stack — what makes "previous" possible on a cursor-paged read.
 *
 * 🔴 CURSORS GO FORWARD ONLY, WHICH IS WHY THIS EXISTS. `AuditReadService` pages
 * by `cursor: { id }` because offsets silently skip rows on an append-only table
 * — *"the row you never see looks exactly like a row that was never written"* —
 * and a forward-only cursor cannot answer "the page before this one". Holding
 * the cursor that OPENED each page lets the screen walk back through pages it
 * has already been given, without asking the server for a backwards read it does
 * not offer.
 *
 * ⚠️ `null` IS PAGE ONE'S CURSOR AND IS A REAL ENTRY IN THE STACK. Modelling it
 * as an absence is how "previous" from page two lands on page two again.
 */
export function pushCursor(
  stack: readonly (string | null)[],
  next: string,
): (string | null)[] {
  return [...stack, next];
}

export function popCursor(
  stack: readonly (string | null)[],
): (string | null)[] {
  /* Never empties: page one's `null` is the floor, so "previous" on page one is
     a no-op rather than a state with no cursor at all. */
  return stack.length <= 1 ? [...stack] : stack.slice(0, -1);
}

/* ──────────────────────────────────────── F2 · reading the lifecycle ── */

/**
 * The lifecycle, as a filter rather than as a column you scan for.
 *
 * 🔴 THE SCREEN SHOWED FIVE STATES AS PLAIN TEXT IN ONE COLUMN OF ONE TABLE, and
 * the question an operator actually arrives with is *"which schools are still
 * onboarding"* — a question you answered by reading forty rows. A status is not
 * a property you skim; it is the thing you are looking for.
 *
 * ⚠️ `CREATED` IS FOLDED INTO `ONBOARDING`, EXACTLY AS `statusCounts` DOES IT ON
 * F1. `SchoolsService.create` never writes `CREATED` — `org.prisma` defaults the
 * column to `ONBOARDING` because *"creating a school through F2 IS the onboard
 * action"* — so a permanently-empty filter would teach an operator a state
 * exists that the product cannot produce. Folding rather than dropping means a
 * row that somehow does have it is never invisible.
 */
export type SchoolFilter = "ALL" | "ONBOARDING" | "ACTIVE" | "SUSPENDED" | "ARCHIVED";

export const SCHOOL_FILTERS: readonly SchoolFilter[] = [
  "ALL",
  "ONBOARDING",
  "ACTIVE",
  "SUSPENDED",
  "ARCHIVED",
];

export function matchesSchoolFilter(
  school: SchoolSummary,
  filter: SchoolFilter,
): boolean {
  if (filter === "ALL") return true;
  if (filter === "ONBOARDING") {
    return school.status === "ONBOARDING" || school.status === "CREATED";
  }
  return school.status === filter;
}

/**
 * How many schools sit in each state, and how many cannot be signed into.
 *
 * 🔴 `noAdmin` IS COUNTED ACROSS EVERY STATE EXCEPT ARCHIVED, AND IT IS THE ONE
 * FIGURE ON THIS SCREEN THAT IS A TASK. `adminCount: 0` is *"the state F2 exists
 * to fix"* (`schools.service.ts`): nobody at that school can sign in at all. An
 * archived school is read-only and is supposed to have nobody, so including it
 * would make the number permanently non-zero and permanently ignorable.
 */
export interface SchoolTotals {
  all: number;
  onboarding: number;
  active: number;
  suspended: number;
  archived: number;
  noAdmin: number;
}

export function summariseSchools(
  rows: readonly SchoolSummary[],
): SchoolTotals {
  const totals: SchoolTotals = {
    all: rows.length,
    onboarding: 0,
    active: 0,
    suspended: 0,
    archived: 0,
    noAdmin: 0,
  };
  for (const row of rows) {
    if (row.status === "ONBOARDING" || row.status === "CREATED") totals.onboarding += 1;
    if (row.status === "ACTIVE") totals.active += 1;
    if (row.status === "SUSPENDED") totals.suspended += 1;
    if (row.status === "ARCHIVED") totals.archived += 1;
    if (row.adminCount === 0 && row.status !== "ARCHIVED") totals.noAdmin += 1;
  }
  return totals;
}

/**
 * The pill tone for a lifecycle state.
 *
 * 🔴 SUSPENDED IS `warning`, NOT `danger`, AND ARCHIVED IS NEITHER.
 * `admin-panel.md` §3: suspension is a **billing** state — nobody can sign in,
 * nothing is deleted, and it is reversible. Colouring it as an emergency would
 * make the one genuinely irreversible state on the screen (archived) the quieter
 * of the two, which is backwards.
 *
 * ⚠️ AND COLOUR IS NEVER THE ONLY SIGNAL. `StatusPill` demands an icon and text
 * with every tone (§12); this decides which of the three, not whether there are
 * three.
 */
export function schoolStatusTone(
  status: SchoolStatus,
): "success" | "warning" | "neutral" {
  if (status === "ACTIVE") return "success";
  if (status === "SUSPENDED") return "warning";
  /* Onboarding and archived are both "not a problem, not finished" — the first
     is a school on its way in, the second one that has stopped. Neither is a
     thing to fix, and a fourth tone would imply one of them was. */
  return "neutral";
}

/* ────────────────────────────────────────────── the System screen ── */

/**
 * A count for the screen, or an em dash.
 *
 * 🔴 AN ABSENT RESPONSE IS NOT A ZERO, AND THE SYSTEM SCREEN USED TO SAY IT WAS.
 * Every figure on that page was written `x ?? 0`, so between mount and first
 * response it rendered a platform with no schools, no seats and no teachers —
 * and an operator opening it during an incident reads that as the outage. The
 * dash is not a nicety; it is the difference between *"nothing"* and *"not yet
 * known"*, which are the two answers that page exists to tell apart.
 */
export function countText(n: number | null): string {
  return n === null ? "—" : n.toLocaleString();
}

/**
 * One slice's share of a total, or `null` when either half is unknown.
 *
 * ⚠️ A ZERO TOTAL YIELDS `null`, NOT `0`. Zero schools is a real state, and
 * "0.0% of them are active" is a sentence about a denominator that does not
 * exist — OD-39/OD-72's rule, which is why the caller prints a bare count in
 * that case rather than a percentage.
 */
export function shareOf(n: number | null, total: number | null): number | null {
  if (n === null || total === null || total === 0) return null;
  return Number(((n / total) * 100).toFixed(1));
}

/**
 * The product-health verdict as a pill tone.
 *
 * 🔴 `TOO_FEW_ROUNDS` IS `neutral`, NEVER `success`. `product-health.ts`
 * withholds the rate below the sample floor precisely because nobody should read
 * it; colouring that green would answer, in the one channel a reader takes in
 * before any words, the exact question the server declined to answer.
 */
export function trackingTone(
  verdict: "HEALTHY" | "INVESTIGATE" | "NOT_WORKING" | "TOO_FEW_ROUNDS",
): "success" | "warning" | "danger" | "neutral" {
  switch (verdict) {
    case "HEALTHY":
      return "success";
    case "INVESTIGATE":
      return "warning";
    case "NOT_WORKING":
      return "danger";
    default:
      return "neutral";
  }
}

/* ────────────────────────────────────── F2 · the onboarding flow ── */

/**
 * `admin-panel.md` §3's onboard action, as four steps and one submit.
 *
 * 🔴 THE WHOLE MODEL IS HERE AND NOT IN THE FORM, WHICH IS THIS FILE'S RULE AND
 * NOT A PREFERENCE. *"A decision made inside a `.tsx` is a decision no test can
 * reach"* — and the decisions below are the ones that decide whether a school
 * ends up billable, whether anybody can sign into it, and how long it is free
 * for. A `disabled={...}` expression inline in a button is all three of those
 * facts written where nothing can assert them.
 *
 * 🔴 IT IS A WIZARD RATHER THAN ONE LONG FORM, AND THE REASON IS THE THIRD STEP.
 * Name and region are unavoidable; an admin is optional; a plan and a trial are
 * optional. A single form makes those three look equally required and hides the
 * one question the operator has to have an answer to — *what happens when the
 * free period ends* — halfway down a scroll, under a password field. Steps make
 * each decision arrive on its own, in the order the phone call takes them.
 */

export type OnboardingStep = "INSTITUTION" | "ADMIN" | "PLAN" | "REVIEW";

export const ONBOARDING_STEPS = [
  "INSTITUTION",
  "ADMIN",
  "PLAN",
  "REVIEW",
] as const satisfies readonly OnboardingStep[];

/**
 * How long a free trial runs, in days, before the form turns it into a date.
 *
 * 🔴 30 IS THE DEFAULT BECAUSE `SIGNUP_TRIAL_DAYS` IS 30. A school that signs
 * itself up gets thirty days; a school an operator onboards on a call getting a
 * different number by accident — because a dropdown happened to list 14 first —
 * is two trial lengths in one product and no decision behind either.
 *
 * 📋 THE OTHER FOUR ARE THIS FILE'S CHOICE AND NOTHING SUPPLIES THEM. They are
 * the shapes a pilot actually takes: a fortnight to look at it, a month as the
 * standard, half a term, a term, a full quarter. The field accepts any number in
 * range, so a preset is a shortcut rather than a limit.
 */
export const TRIAL_DAY_PRESETS = [14, 30, 45, 60, 90] as const;
export const DEFAULT_TRIAL_DAYS = 30;

/**
 * 🔴 A BOUND ON BOTH ENDS. Zero days is a trial that ended before the mail
 * announcing it arrived; a four-digit one is the open-ended free customer
 * `billing.dto.ts` describes, entered by a fat finger rather than by a decision.
 * A year is long enough for any pilot somebody would defend out loud.
 */
export const TRIAL_DAYS_MIN = 1;
export const TRIAL_DAYS_MAX = 365;

/** Mirrors `subscriptionBlock.seatLimit` on the server. */
export const SEAT_LIMIT_MAX = 100_000;

/**
 * Everything the wizard collects, in one object.
 *
 * ⚠️ EVERY FIELD IS A STRING EXCEPT THE TWO THAT ARE GENUINELY NOT. This is what
 * the inputs hold, not what the request carries — `onboardingBody` is the one
 * place the two are reconciled, so a field that is blank on screen cannot reach
 * the server as a `0` or an empty-string id.
 */
export interface OnboardingDraft {
  name: string;
  region: Region;
  /** Blank for both means "no admin yet", which is a real way to finish. */
  adminName: string;
  adminEmail: string;
  delivery: "EMAIL" | "PASSWORD";
  password: string;
  confirm: string;
  /** Blank means "no plan yet", which is also a real way to finish. */
  planId: string;
  trialDays: number;
  /** Blank means uncapped. `SeatsService` reads a missing limit that way. */
  seatLimit: string;
  note: string;
}

export function emptyOnboardingDraft(region: Region): OnboardingDraft {
  return {
    name: "",
    region,
    adminName: "",
    adminEmail: "",
    /*
     * 🔴 `EMAIL` IS THE DEFAULT, AND THAT IS CHANGE 19 §1's WHOLE POINT: it is
     * the only path where no human — not the operator, not this browser, not a
     * log — ever holds a credential that works.
     */
    delivery: "EMAIL",
    password: "",
    confirm: "",
    planId: "",
    trialDays: DEFAULT_TRIAL_DAYS,
    seatLimit: "",
    note: "",
  };
}

/**
 * Is this draft asking for an admin at all?
 *
 * ⚠️ BOTH FIELDS, NOT EITHER. A half-typed name with no address is somebody who
 * started and moved on, and treating it as intent produces a 400 about a field
 * they never finished.
 */
export function wantsAdmin(draft: OnboardingDraft): boolean {
  return draft.adminName.trim().length > 0 && draft.adminEmail.trim().length > 0;
}

/** Is this draft asking for a plan and a trial? */
export function wantsSubscription(draft: OnboardingDraft): boolean {
  return draft.planId.trim().length > 0;
}

/**
 * 🔴 DELIBERATELY LOOSER THAN THE SERVER'S `z.email()`, AND THE ASYMMETRY IS THE
 * POINT.
 *
 * `staff-auth.dto.ts` argues that a validator stricter than the one that stored
 * an address turns a login into a 400. The mirror of that argument applies to a
 * form: a browser-side rule stricter than the server's refuses an address the
 * server would have accepted, and the operator has no way to find out which of
 * them is wrong. So this catches the three shapes that are unambiguously a typo
 * — no `@`, nothing before it, no dot after it — and lets the server be the
 * authority on everything else.
 */
export function emailLooksTypedWrong(raw: string): boolean {
  const value = raw.trim();
  if (value.length === 0) return true;
  const at = value.indexOf("@");
  if (at < 1) return true;
  if (value.indexOf("@", at + 1) !== -1) return true;
  const domain = value.slice(at + 1);
  return !domain.includes(".") || domain.startsWith(".") || domain.endsWith(".");
}

/**
 * What is stopping this step, as sentences an operator can act on.
 *
 * 🔴 SENTENCES RATHER THAN A BOOLEAN, AND THE DIFFERENCE IS A SUPPORT CALL. A
 * disabled Next button with no explanation is a form that has decided something
 * about your input and will not say what — `passwordState`'s `'problem'` arm
 * makes the same argument one field down.
 *
 * ⚠️ EMPTY MEANS THE STEP IS SATISFIED, so `canAdvance` is a length check and
 * there is exactly one rule set rather than two that can disagree.
 */
export function stepBlockers(
  step: OnboardingStep,
  draft: OnboardingDraft,
  password: PasswordState,
): readonly string[] {
  const problems: string[] = [];

  switch (step) {
    case "INSTITUTION":
      if (draft.name.trim().length === 0) {
        problems.push("The school needs a name.");
      }
      break;

    case "ADMIN": {
      /*
       * ⚠️ NO ADMIN AT ALL IS A COMPLETE, VALID STEP. A school created from a
       * signed contract before anybody has been named is real, and F2's list
       * sorts that row to the top precisely so it gets picked up.
       */
      if (!wantsAdmin(draft)) {
        /* One field filled and not the other is the half-finished case. */
        const started =
          draft.adminName.trim().length > 0 || draft.adminEmail.trim().length > 0;
        if (started) {
          problems.push("An admin needs both a name and an email address.");
        }
        break;
      }
      if (emailLooksTypedWrong(draft.adminEmail)) {
        problems.push("That does not look like an email address.");
      }
      /*
       * 🔴 ONLY THE PASSWORD PATH CAN BE BLOCKED ON A PASSWORD. On the emailed
       * path nobody chooses one — the admin picks their own from the link — so
       * a password rule applied here would block the safer of the two routes.
       */
      if (draft.delivery === "PASSWORD" && password.kind !== "ready") {
        problems.push("Choose a password for them, or email them a set-up link instead.");
      }
      break;
    }

    case "PLAN": {
      /* No plan is a valid answer — F3 exists for the school that gets one later. */
      if (!wantsSubscription(draft)) break;
      if (!Number.isInteger(draft.trialDays)) {
        problems.push("The trial length has to be a whole number of days.");
      } else if (draft.trialDays < TRIAL_DAYS_MIN || draft.trialDays > TRIAL_DAYS_MAX) {
        problems.push(
          `A free trial runs between ${String(TRIAL_DAYS_MIN)} and ${String(TRIAL_DAYS_MAX)} days.`,
        );
      }
      const seats = draft.seatLimit.trim();
      if (seats.length > 0) {
        const n = Number(seats);
        if (!Number.isInteger(n) || n < 1 || n > SEAT_LIMIT_MAX) {
          problems.push(
            `A place limit is a whole number between 1 and ${String(SEAT_LIMIT_MAX)}, or blank for no limit.`,
          );
        }
      }
      break;
    }

    case "REVIEW":
      /*
       * 🔴 THE LAST STEP RE-RUNS EVERY EARLIER ONE, AND IT IS NOT BELT AND
       * BRACES. A draft can become invalid after the step that owned it was
       * passed — the operator goes back and clears the school name, or switches
       * delivery to PASSWORD without typing one — and a review screen that
       * trusted "they already got past that step" would submit it.
       */
      for (const earlier of ONBOARDING_STEPS) {
        if (earlier === "REVIEW") break;
        problems.push(...stepBlockers(earlier, draft, password));
      }
      break;
  }

  return problems;
}

export function canAdvance(
  step: OnboardingStep,
  draft: OnboardingDraft,
  password: PasswordState,
): boolean {
  return stepBlockers(step, draft, password).length === 0;
}

/**
 * ⚠️ THE STEP AFTER THIS ONE, OR `null` AT THE END. A component that did
 * `STEPS[i + 1]` would read one past the array and hand `undefined` to a
 * setState typed as a step, which renders as a blank panel rather than an error.
 */
export function nextStep(step: OnboardingStep): OnboardingStep | null {
  const i = ONBOARDING_STEPS.indexOf(step);
  return i === -1 || i === ONBOARDING_STEPS.length - 1 ? null : ONBOARDING_STEPS[i + 1];
}

export function previousStep(step: OnboardingStep): OnboardingStep | null {
  const i = ONBOARDING_STEPS.indexOf(step);
  return i <= 0 ? null : ONBOARDING_STEPS[i - 1];
}

/**
 * When the free trial ends, computed here so the operator agrees to a date.
 *
 * 🔴 THE SERVER TAKES A DATE AND NOT A NUMBER OF DAYS, and `billing.dto.ts` says
 * why: *"'30 days' has to be resolved against a clock somewhere, and doing it on
 * the server means the operator is agreeing to a date they cannot see."* This is
 * that clock, and the date it produces is the one on the review screen.
 *
 * ⚠️ WHOLE DAYS OF MILLISECONDS, NOT `setMonth`/`setDate` ARITHMETIC —
 * `school-signup.service.ts`'s `addDays` makes the identical choice: "30 days"
 * is not a calendar word, and reaching for a month helper makes a trial started
 * on 31 January end on a date nobody asked for.
 */
export function trialEndsOn(from: Date, days: number): Date {
  return new Date(from.getTime() + days * 86_400_000);
}

/**
 * The draft, as the request body.
 *
 * 🔴 ONE PLACE WHERE BLANK BECOMES ABSENT. Three fields mean *"leave this
 * alone"* when empty, and each of them means something different and wrong if it
 * travels as a falsy value instead: no `admin` block at all vs an admin with no
 * name; no `subscription` block vs a plan whose id is `""`; and — the expensive
 * one — no `seatLimit` key vs `seatLimit: 0`, which is a school that may enrol
 * nobody.
 *
 * ⚠️ IT TAKES `now` RATHER THAN READING THE CLOCK. The trial's end date is
 * computed from it, and a function that called `new Date()` internally could not
 * be asserted against a fixed date — which is the only assertion worth making
 * about it.
 */
export function onboardingBody(
  draft: OnboardingDraft,
  password: PasswordState,
  now: Date,
): CreateSchoolBody {
  const body: CreateSchoolBody = {
    name: draft.name.trim(),
    region: draft.region,
  };

  if (wantsAdmin(draft)) {
    const email = draft.adminEmail.trim();
    const name = draft.adminName.trim();
    body.admin =
      draft.delivery === "EMAIL" || password.kind !== "ready"
        ? { delivery: "EMAIL", email, name }
        : { delivery: "PASSWORD", email, name, password: password.password };
  }

  if (wantsSubscription(draft)) {
    const seats = draft.seatLimit.trim();
    const note = draft.note.trim();
    body.subscription = {
      planId: draft.planId,
      trialEndsAt: trialEndsOn(now, draft.trialDays).toISOString(),
      /* Absent, not zero — see the header. */
      ...(seats.length > 0 ? { seatLimit: Number(seats) } : {}),
      ...(note.length > 0 ? { note } : {}),
    };
  }

  return body;
}

/**
 * What the result screen has to tell the operator, ranked by what it costs them
 * to not know.
 *
 * 🔴 THE ORDER IS THE MESSAGE. A school was created either way — that is never
 * in doubt by the time this runs — so the loudest thing on the screen must be
 * whichever of the two follow-ups did not happen. An operator who closes the tab
 * believing a head teacher has a link they never received is the failure change
 * 19 §1 exists to end, and it is worse than a missing plan because nobody at the
 * school can tell you about it: they cannot log in to complain.
 */
export type OnboardingOutcome =
  /** The mail did not go. Somebody must press re-send. */
  | { kind: "MAIL_FAILED" }
  /** An operator-chosen password: they are holding it and must hand it over. */
  | { kind: "HAND_OVER_PASSWORD" }
  /** Everything asked for happened. */
  | { kind: "DONE" };

export function onboardingOutcome(created: CreatedSchool): OnboardingOutcome {
  if (created.admin === null) return { kind: "DONE" };
  if (created.admin.passwordSource === "OPERATOR") return { kind: "HAND_OVER_PASSWORD" };
  return created.admin.delivery === "SENT" ? { kind: "DONE" } : { kind: "MAIL_FAILED" };
}

/**
 * 🔴 WHETHER THE RE-SEND BUTTON MAY EVEN BE OFFERED.
 *
 * `resendAdminInvite` refuses an admin who has already set a password with a
 * 400, and D4's lesson — *"the UI must not offer a promote button that no-ops or
 * throws"* — applies verbatim. The one case where it is legitimate is the one
 * the server accepts: an account created by invite that nobody has claimed.
 */
export function canResendInvite(admin: ProvisionedAdmin | null): boolean {
  return admin !== null && admin.passwordSource === "INVITE";
}
